// TODO: https://github.com/combinaut/sparkle/issues/33514 moves the legacy jQuery requests to clients that send the locale and deletes this bridge
import "#legacy/utilities/request_locale.js";

import { getApiHost } from "#/shared/api/api-host";

const INSTALLED = Symbol.for("sparkle.localeAjaxPrefilter");

/**
 * Adds the page locale (`Sparkle.Utilities.requestLocale()`) as `?locale=` to every jQuery request
 * bound for Sparkle that does not already name one, so legacy fetches are served in the page's
 * language. The server takes a data request's locale from its path prefix or that param alone.
 *
 * A request is Sparkle-bound when its origin is one of the Sparkle API hosts in
 * `SparkleConfig.embedPublishingDomains` or the bundle's API host (`getApiHost`). On a host CMS page that
 * leaves the host's own requests untouched. Nothing is added on a default-locale page, nor to a
 * request whose path already carries a locale prefix.
 *
 * Installs once per jQuery instance, since several Sparkle bundles can share the page's jQuery.
 *
 * @param {JQueryStatic} jQuery
 */
export function installLocaleAjaxPrefilter(jQuery) {
  if (!jQuery?.ajaxPrefilter || jQuery[INSTALLED]) {
    return;
  }

  jQuery[INSTALLED] = true;
  jQuery.ajaxPrefilter(addRequestLocale);
}

function addRequestLocale(options) {
  const locale = window.Sparkle?.Utilities?.requestLocale?.();
  if (!locale) {
    return;
  }

  // A relative URL is resolved as the XHR resolves it: against the document base, which a host CMS layout
  // preview points at the host's server.
  let url;
  try {
    url = new URL(options.url, document.baseURI);
  } catch {
    return;
  }

  if (!isSparkleBound(url) || namesLocale(url, options.data)) {
    return;
  }

  url.searchParams.set("locale", locale);
  options.url = url.toString();
}

// By the time prefilters run, jQuery has serialized `data` to a query string, which it appends to
// the URL of a GET afterwards.
function namesLocale(url, data) {
  if (url.searchParams.has("locale") || localePrefixed(url)) {
    return true;
  }

  return typeof data === "string" && new URLSearchParams(data).has("locale");
}

function localePrefixed(url) {
  const available = window.SparkleConfig?.locales?.available ?? [];

  return available.includes(url.pathname.split("/")[1]);
}

function isSparkleBound(url) {
  return sparkleOrigins().includes(url.origin);
}

function sparkleOrigins() {
  const origins = (window.SparkleConfig?.embedPublishingDomains ?? []).map((domain) => domain.apiHost);

  const { apiHost } = getApiHost();
  try {
    if (apiHost) {
      origins.push(new URL(apiHost).origin);
    }
  } catch {
    // An unparseable API host names no origin.
  }

  return origins;
}
